import { DOCUMENT_HTML_ATTRIBUTES } from '../../constants';
import { BUILDING_CLASS, STATIC_ELEMENT_ID_ATTR } from '../constants';
import { SECTION_HTML_SELECTOR } from '../../tiptap/nodes/section/constants';
import { currentEditor } from './liveEditor';
import { trace } from '../trace';

export type TAnchor = {
  scroller: HTMLElement;
  elementId: string;
  offsetTop: number;
  /** Where the page was when the click happened - what to fall back to if the element is not found. */
  scrollTop: number;
};

/** Measured from the CLICK: a swap changes the page height three times and each one moves scrollTop. */
const ANCHOR_HOLD_MS = 2500;

// `requestAnimationFrame` barely runs during a swap, so a timer keeps the correction close to the change.
const ANCHOR_TICK_MS = 100;

// Not `mousedown`: a click does not scroll, and the swap it may start ends this hold anyway.
const ABORTING_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

// The browser scrolls the caret into view for these, so the hold has to let go.
const SCROLLING_KEYS = new Set([
  'PageUp',
  'PageDown',
  'Home',
  'End',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  ' ',
]);
const scrollsTheDocument = (event: KeyboardEvent): boolean => SCROLLING_KEYS.has(event.key);

// A heading carries its id as a plain `id`; everything else carries it as `data-id`.
const elementSelector = (id: string) =>
  Object.values(DOCUMENT_HTML_ATTRIBUTES)
    .map((attribute) => `[${attribute}="${CSS.escape(id)}"]`)
    .join(',');

/**
 * The element to line the page up against, preferring the editor's copy - EXCEPT while the editor is
 * rebuilding.
 *
 * A rebuilding editor is `position: absolute; top: 0`, so everything inside it reports a rect at the
 * top of the container. Lining up against that asks for a scroll position of roughly minus the
 * anchor's offset, the browser clamps it to 0, and the page sits at the very top until the swap
 * finishes and corrects it - which is the page appearing to scroll twice. While it is out of
 * flow, the static copy is the only one whose geometry means anything.
 */
// Everything below such a host is a sibling element, not part of the element the walk started from.
const CONTENT_HOST_SELECTOR = `[data-node-view-content],[data-node-view-content-react],${SECTION_HTML_SELECTOR}`;

// In the editor an id can sit on an inner node, and a section's node view sits above the element.
export const elementLevel = (node: HTMLElement | null, dom: HTMLElement | undefined): HTMLElement | null => {
  let current = node;
  while (
    current?.parentElement &&
    current.parentElement !== dom &&
    !current.parentElement.matches(CONTENT_HOST_SELECTOR)
  )
    current = current.parentElement;
  return current;
};

const anchorElement = (elementId: string): HTMLElement | null => {
  const dom = currentEditor()?.view.dom as HTMLElement | undefined;
  const inEditor =
    dom && !dom.classList.contains(BUILDING_CLASS) ? dom.querySelector<HTMLElement>(elementSelector(elementId)) : null;
  return (
    elementLevel(inEditor, dom) ??
    document.querySelector<HTMLElement>(`[${STATIC_ELEMENT_ID_ATTR}="${CSS.escape(elementId)}"]`)
  );
};

// Callers must not measure the element themselves - the correction resolves the element around the id, not the node carrying it.
export const anchorOffsetTop = (scroller: HTMLElement, elementId: string): number | null => {
  const element = anchorElement(elementId);
  return element ? element.getBoundingClientRect().top - scroller.getBoundingClientRect().top : null;
};

let lastScroller: HTMLElement | null = null;

export const rememberScroller = (scroller: HTMLElement) => {
  lastScroller = scroller;
};

const scrollerFor = (): HTMLElement | null => {
  if (lastScroller?.isConnected) return lastScroller;
  // A reader who only scrolled never went through a click or a scroll target, so nothing handed one over.
  let element = currentEditor()?.view.dom.parentElement ?? null;
  while (element) {
    const overflow = getComputedStyle(element).overflowY;
    if ((overflow === 'auto' || overflow === 'scroll') && element.scrollHeight > element.clientHeight + 1) {
      lastScroller = element;
      return element;
    }
    element = element.parentElement;
  }
  return null;
};

let letBrowserMoveThePage: (() => void) | null = null;

export const pauseBrowserScrollAdjust = () => {
  if (letBrowserMoveThePage) return;
  const scroller = scrollerFor();
  if (!scroller) return;
  const previous = scroller.style.overflowAnchor;
  scroller.style.overflowAnchor = 'none';
  letBrowserMoveThePage = () => {
    scroller.style.overflowAnchor = previous;
    letBrowserMoveThePage = null;
  };
};

export const resumeBrowserScrollAdjust = () => letBrowserMoveThePage?.();

// Leaving edit mode rebuilds the editor, and the swap that follows has no anchor of its own.
export const viewportAnchor = (): TAnchor | null => {
  const scroller = scrollerFor();
  if (!scroller) return null;
  const top = scroller.getBoundingClientRect().top;
  // A node view wraps its node in a div of its own, and that wrapper carries no `data-id`.
  const elements = document.querySelectorAll<HTMLElement>(`[${STATIC_ELEMENT_ID_ATTR}], .ProseMirror > *`);
  for (const element of elements) {
    const rect = element.getBoundingClientRect();
    if (rect.bottom <= top) continue;
    // A reusable block's own content carries ids too, so its wrapper's id has to win over them.
    const ownId = element.getAttribute(STATIC_ELEMENT_ID_ATTR) ?? element.getAttribute('data-id') ?? element.id;
    const elementId =
      // A live heading renders its id on the heading element inside the node view wrapper.
      ownId || element.querySelector('[data-id]')?.getAttribute('data-id') || element.querySelector('[id]')?.id;
    if (!elementId) continue;
    return { scroller, elementId, offsetTop: rect.top - top, scrollTop: scroller.scrollTop };
  }
  return null;
};

let holdToken = 0;

// Held here so the code that CHANGES the layout can correct it in the same task, not a frame later.
let activeHold: TAnchor | null = null;

// Call synchronously after a step that changes the page height - one frame later is a visible flash.
export const correctAnchor = (anchor: TAnchor): { moved: number; found: boolean } => {
  const element = anchorElement(anchor.elementId);
  if (!element) {
    const delta = anchor.scroller.scrollTop - anchor.scrollTop;
    if (Math.abs(delta) > 1) anchor.scroller.scrollTop = anchor.scrollTop;
    return { moved: Math.abs(Math.round(delta)), found: false };
  }
  const delta = element.getBoundingClientRect().top - anchor.scroller.getBoundingClientRect().top - anchor.offsetTop;
  if (Math.abs(delta) <= 1) return { moved: 0, found: true };
  const scrollBefore = anchor.scroller.scrollTop;
  anchor.scroller.scrollTop += delta;
  trace('anchor:corrected', {
    elementId: anchor.elementId,
    wantedOffsetTop: Math.round(anchor.offsetTop),
    offsetTopBefore: Math.round(delta + anchor.offsetTop),
    offsetTopAfter: Math.round(element.getBoundingClientRect().top - anchor.scroller.getBoundingClientRect().top),
    scrollBefore: Math.round(scrollBefore),
    scrollAfter: Math.round(anchor.scroller.scrollTop),
  });
  return { moved: Math.abs(Math.round(delta)), found: true };
};

/** Ends any running hold, so a new swap's corrections cannot fight an older one. */
export const endHold = () => {
  holdToken++;
  activeHold = null;
};

export const forgetScroller = () => {
  lastScroller = null;
};

// `stillInDocument` only labels the trace - it separates a deleted element from one nothing rendered.
export const holdAnchor = (anchor: TAnchor, stillInDocument?: (elementId: string) => boolean) => {
  const token = ++holdToken;
  activeHold = anchor;
  const started = performance.now();
  let frames = 0;
  let corrections = 0;
  let drift = 0;
  let missing = 0;

  const ticker = window.setInterval(() => {
    if (token !== holdToken) return stop();
    correctAnchor(anchor);
  }, ANCHOR_TICK_MS);

  const stop = () => {
    window.clearInterval(ticker);
    ABORTING_EVENTS.forEach((event) => window.removeEventListener(event, onUserInput, { capture: true }));
  };
  const onUserInput = (event: Event) => {
    if (!event.isTrusted) return;
    if (event.type === 'keydown' && !scrollsTheDocument(event as KeyboardEvent)) return;
    if (token === holdToken) holdToken++;
    if (activeHold === anchor) activeHold = null;
    trace('anchor:aborted', { by: event.type, frames, corrections, driftPx: drift });
    stop();
  };
  ABORTING_EVENTS.forEach((event) => window.addEventListener(event, onUserInput, { capture: true, passive: true }));

  const step = () => {
    if (token !== holdToken) return stop();
    frames++;
    const { moved, found } = correctAnchor(anchor);
    if (!found) missing++;
    if (moved) {
      corrections++;
      drift += moved;
    }
    if (performance.now() - started < ANCHOR_HOLD_MS) return requestAnimationFrame(step);
    trace('anchor:held', {
      elementId: anchor.elementId,
      wantedOffsetTop: Math.round(anchor.offsetTop),
      frames,
      corrections,
      totalDriftPx: drift,
      framesElementMissing: missing,
      stillInDocument: missing > 0 ? stillInDocument?.(anchor.elementId) : undefined,
      ms: Math.round(performance.now() - started),
    });
    if (activeHold === anchor) activeHold = null;
    stop();
  };
  trace('anchor:hold-start', { elementId: anchor.elementId, wantedOffsetTop: Math.round(anchor.offsetTop) });
  step();
};

export const correctActiveHold = () => {
  if (activeHold) correctAnchor(activeHold);
};
